/**
 * 極簡 JSON Schema 驗證器。對應 docs/protocol.md P5。
 *
 * 刻意只支援協定用得到的子集，換取零依賴。
 * 支援：type / enum / const / minimum / maximum / minLength / maxLength /
 *       pattern / properties / required / additionalProperties / items /
 *       minItems / maxItems / nullable
 *
 * 不支援（遇到會直接忽略）：$ref、allOf/anyOf/oneOf、if-then-else、format。
 */

import type { JSONSchema } from './types.ts'

export interface Violation {
  at: string
  message: string
}

export function validateValue(schema: JSONSchema | undefined, value: unknown): Violation[] {
  if (!schema) return []
  const violations: Violation[] = []
  check(schema, value, '$', violations)
  return violations
}

function check(schema: JSONSchema, value: unknown, at: string, out: Violation[]): void {
  if (value === null && schema.nullable === true) return

  if (schema.const !== undefined && !deepEqual(value, schema.const)) {
    out.push({ at, message: `必須等於 ${JSON.stringify(schema.const)}` })
    return
  }

  if (schema.enum !== undefined) {
    if (!schema.enum.some((candidate) => deepEqual(candidate, value))) {
      out.push({ at, message: `必須是下列之一：${schema.enum.map((v) => JSON.stringify(v)).join(', ')}` })
      return
    }
  }

  if (schema.type !== undefined && !matchesType(schema.type, value)) {
    out.push({ at, message: `型別應為 ${schema.type}，實際為 ${describe(value)}` })
    return
  }

  switch (schema.type) {
    case 'string':
      checkString(schema, value as string, at, out)
      break
    case 'number':
    case 'integer':
      checkNumber(schema, value as number, at, out)
      break
    case 'object':
      checkObject(schema, value as Record<string, unknown>, at, out)
      break
    case 'array':
      checkArray(schema, value as unknown[], at, out)
      break
    default:
      break
  }
}

function checkString(schema: JSONSchema, value: string, at: string, out: Violation[]): void {
  if (schema.minLength !== undefined && value.length < schema.minLength) {
    out.push({ at, message: `長度至少 ${schema.minLength}，實際 ${value.length}` })
  }
  if (schema.maxLength !== undefined && value.length > schema.maxLength) {
    out.push({ at, message: `長度至多 ${schema.maxLength}，實際 ${value.length}` })
  }
  if (schema.pattern !== undefined && !new RegExp(schema.pattern).test(value)) {
    out.push({ at, message: `不符合樣式 ${schema.pattern}` })
  }
}

function checkNumber(schema: JSONSchema, value: number, at: string, out: Violation[]): void {
  if (schema.minimum !== undefined && value < schema.minimum) {
    out.push({ at, message: `不得小於 ${schema.minimum}，實際 ${value}` })
  }
  if (schema.maximum !== undefined && value > schema.maximum) {
    out.push({ at, message: `不得大於 ${schema.maximum}，實際 ${value}` })
  }
}

function checkObject(
  schema: JSONSchema,
  value: Record<string, unknown>,
  at: string,
  out: Violation[],
): void {
  for (const key of schema.required ?? []) {
    if (!Object.hasOwn(value, key)) {
      out.push({ at: `${at}.${key}`, message: '必填欄位缺漏' })
    }
  }
  const properties = schema.properties
  if (properties) {
    for (const [key, sub] of Object.entries(properties)) {
      if (Object.hasOwn(value, key)) check(sub, value[key], `${at}.${key}`, out)
    }
    if (schema.additionalProperties === false) {
      for (const key of Object.keys(value)) {
        if (!Object.hasOwn(properties, key)) {
          out.push({ at: `${at}.${key}`, message: '不允許的額外欄位' })
        }
      }
    }
  }
}

function checkArray(schema: JSONSchema, value: unknown[], at: string, out: Violation[]): void {
  if (schema.minItems !== undefined && value.length < schema.minItems) {
    out.push({ at, message: `至少 ${schema.minItems} 個元素，實際 ${value.length}` })
  }
  if (schema.maxItems !== undefined && value.length > schema.maxItems) {
    out.push({ at, message: `至多 ${schema.maxItems} 個元素，實際 ${value.length}` })
  }
  if (schema.items) {
    for (let i = 0; i < value.length; i++) check(schema.items, value[i], `${at}[${i}]`, out)
  }
}

function matchesType(type: NonNullable<JSONSchema['type']>, value: unknown): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
    case 'boolean':
      return typeof value === 'boolean'
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value)
    case 'array':
      return Array.isArray(value)
    case 'null':
      return value === null
    default:
      return true
  }
}

function describe(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (typeof a !== 'object') return false

  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  const ak = Object.keys(ao)
  const bk = Object.keys(bo)
  if (ak.length !== bk.length) return false
  return ak.every((k) => Object.hasOwn(bo, k) && deepEqual(ao[k], bo[k]))
}
